import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useBankCodeSearch,
  type BankCodeOption,
} from '@/hooks/react-query/useBankCodes';
import { cn } from '@/lib/utils';

interface BankNameSelectProps {
  /** Current bank name; shown on the trigger even if it predates the registry. */
  value: string;
  /** Scopes the search to the bank's country when set. */
  countryCode?: string;
  /** Called with the picked registry bank. Registry-only: no free-text commit. */
  onSelect: (bank: BankCodeOption) => void;
  id?: string;
  /**
   * When true the IBAN resolved to a known registry bank, so the name is the
   * authoritative registry value and is shown read-only.
   */
  locked?: boolean;
  /** Extra classes for the trigger (e.g. an error border). */
  className?: string;
}

function getSearchStatus({
  isLoading,
  resultCount,
  search,
}: {
  isLoading: boolean;
  resultCount: number;
  search: string;
}): string | null {
  if (isLoading) return 'Searching...';
  if (resultCount > 0) return null;
  if (search) return 'No matching bank in the registry.';
  return 'Type to search the bank registry.';
}

// Registry-only searchable dropdown for the bank name. Queries the bank-code
// registry as the user types (scoped to the bank country) and only lets a bank
// from the registry be chosen, so the stored name always matches a known bank.
export function BankNameSelect({
  value,
  countryCode,
  onSelect,
  id = 'bank_name',
  locked = false,
  className,
}: BankNameSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<Array<HTMLDivElement | null>>([]);

  // Debounce the query so a keystroke burst hits the API once, not per letter.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: banks = [], isLoading } = useBankCodeSearch({
    search: debouncedSearch,
    country_code: countryCode || undefined,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearch('');
        setHighlightedIndex(0);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    const node = optionsRef.current[highlightedIndex];
    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  // Matched a registry bank via the IBAN: the name is the admin-managed registry
  // value, so lock it rather than let it drift into a typo'd variant.
  if (locked) {
    return (
      <Input
        id={id}
        value={value}
        readOnly
        className={cn('cursor-not-allowed', className)}
      />
    );
  }

  const closeDropdown = () => {
    setOpen(false);
    setSearch('');
    setHighlightedIndex(0);
    buttonRef.current?.focus();
  };

  const handleSelect = (bank: BankCodeOption) => {
    onSelect(bank);
    closeDropdown();
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          banks.length === 0 ? -1 : (prev + 1) % banks.length
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          banks.length === 0 ? -1 : (prev - 1 + banks.length) % banks.length
        );
        break;
      case 'Enter': {
        event.preventDefault();
        const highlighted = banks[highlightedIndex];
        if (highlighted) handleSelect(highlighted);
        break;
      }
      case 'Escape':
        event.preventDefault();
        closeDropdown();
        break;
    }
  };

  const statusMessage = getSearchStatus({
    isLoading,
    resultCount: banks.length,
    search: debouncedSearch,
  });

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        id={id}
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn(
          'w-full justify-between font-normal',
          !value && 'text-muted-foreground',
          className
        )}
        onClick={() => {
          if (open) setHighlightedIndex(0);
          setOpen(!open);
        }}
      >
        <span className="truncate">{value || 'e.g. ING Bank N.V.'}</span>
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-hidden"
        >
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-11 w-full border-0 bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground focus:outline-hidden focus:ring-0"
              placeholder="Search the bank registry..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setHighlightedIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
              autoFocus
            />
          </div>

          <div role="listbox" className="max-h-[300px] overflow-auto p-1">
            {statusMessage ? (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                {statusMessage}
              </div>
            ) : (
              banks.map((bank, index) => (
                <div
                  key={bank.id}
                  ref={(node) => {
                    optionsRef.current[index] = node;
                  }}
                  role="option"
                  aria-selected={value === bank.bank_name}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleSelect(bank)}
                  className={cn(
                    'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground',
                    highlightedIndex === index &&
                      'bg-accent text-accent-foreground'
                  )}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      value === bank.bank_name ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="truncate">{bank.bank_name}</span>
                  <span className="ml-auto pl-2 text-xs text-muted-foreground">
                    {bank.country_code}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
